import {
  Button,
  ChakraSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
} from '@elkaso-app/web-design';
import { useCreateDocumentTypeApi } from 'apis/document-vault/use-create-document-type-api';
import { useUpdateDocumentTypeApi } from 'apis/document-vault/use-update-document-type-api';
import React, { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

const categoryOptions = [
  { value: 'BUSINESS_REGISTRATION', label: 'Business Registration' },
  { value: 'OWNER_IDENTIFICATION', label: 'Owner Identification' },
  { value: 'FINANCIAL_DOCUMENTS', label: 'Financial Documents' },
];

interface FormData {
  name: string;
  category: { value: string; label: string } | null;
  isRequired: boolean;
  hasExpiry: boolean;
  sortOrder: number;
}

interface DocumentTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  documentType?: any;
}

export const DocumentTypeModal: React.FC<DocumentTypeModalProps> = ({ isOpen, onClose, mode, documentType }) => {
  const isEdit = mode === 'edit';

  const { mutate: createDocumentType, isLoading: isCreating } = useCreateDocumentTypeApi({
    onSuccess: () => {
      reset();
      onClose();
    },
  });

  const { mutate: updateDocumentType, isLoading: isUpdating } = useUpdateDocumentTypeApi({
    onSuccess: () => {
      reset();
      onClose();
    },
  });

  const isSubmitting = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      category: null,
      isRequired: true,
      hasExpiry: false,
      sortOrder: 0,
    },
  });

  useEffect(() => {
    if (isEdit && documentType) {
      reset({
        name: documentType.name ?? '',
        category: documentType.category
          ? categoryOptions.find((opt) => opt.value === documentType.category) ?? null
          : null,
        isRequired: documentType.isRequired ?? true,
        hasExpiry: documentType.hasExpiry ?? false,
        sortOrder: documentType.sortOrder ?? 0,
      });
    } else {
      reset({
        name: '',
        category: null,
        isRequired: true,
        hasExpiry: false,
        sortOrder: 0,
      });
    }
  }, [isEdit, documentType, reset, isOpen]);

  const onSubmit = useCallback(
    (data: FormData) => {
      const payload = {
        name: data.name.trim(),
        category: data.category?.value ?? '',
        isRequired: data.isRequired,
        hasExpiry: data.hasExpiry,
        sortOrder: Number(data.sortOrder),
      };

      if (isEdit && documentType) {
        updateDocumentType({ id: documentType.id, body: payload });
      } else {
        createDocumentType({ body: payload });
      }
    },
    [isEdit, documentType, createDocumentType, updateDocumentType]
  );

  const onCloseModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='lg' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit Document Type' : 'Add New Document Type'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl isInvalid={!!errors.name} isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                placeholder='e.g. Trade License'
                {...register('name', {
                  required: 'Name is required',
                  maxLength: { value: 255, message: 'Name must be 255 characters or less' },
                  validate: (value) => value.trim().length > 0 || 'Name cannot be empty',
                })}
              />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>

            <Controller
              control={control}
              name='category'
              rules={{ required: 'Category is required' }}
              render={({ field: { onChange, onBlur, value } }) => (
                <FormControl isInvalid={!!errors.category} isRequired>
                  <FormLabel>Category</FormLabel>
                  <ChakraSelect
                    placeholder='Select category'
                    options={categoryOptions}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                  />
                  <FormErrorMessage>{errors.category?.message}</FormErrorMessage>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name='isRequired'
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  <HStack justify='space-between'>
                    <FormLabel mb={0}>Required</FormLabel>
                    <Switch isChecked={value} onChange={(e) => onChange(e.target.checked)} colorScheme='green' />
                  </HStack>
                  <Text fontSize='xs' color='gray.500'>
                    Whether restaurants must upload this document
                  </Text>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name='hasExpiry'
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  <HStack justify='space-between'>
                    <FormLabel mb={0}>Has Expiry</FormLabel>
                    <Switch isChecked={value} onChange={(e) => onChange(e.target.checked)} colorScheme='orange' />
                  </HStack>
                  <Text fontSize='xs' color='gray.500'>
                    Whether OCR should extract expiry dates from this document
                  </Text>
                </FormControl>
              )}
            />

            <FormControl isInvalid={!!errors.sortOrder}>
              <FormLabel>Sort Order</FormLabel>
              <Input
                type='number'
                placeholder='0'
                {...register('sortOrder', {
                  valueAsNumber: true,
                  min: { value: 0, message: 'Sort order must be 0 or greater' },
                })}
              />
              <FormErrorMessage>{errors.sortOrder?.message}</FormErrorMessage>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant='outline' mr={3} onClick={onCloseModal} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            colorScheme='red'
            onClick={handleSubmit(onSubmit)}
            isLoading={isSubmitting}
            loadingText={isEdit ? 'Updating...' : 'Creating...'}
            isDisabled={!isValid}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
